<template>
  <slot-closure-dialog-shell
    :value="value"
    title="新增休診日"
    max-width="900"
    :dirty="dates.length > 0"
    :saving="saving"
    @input="$emit('input', $event)"
  >
    <div class="d-flex align-baseline flex-wrap mb-2" style="gap: 4px 12px;">
      <div class="font-weight-bold">選擇日期</div>
      <div v-if="copySource" class="text-caption grey--text text--darken-1">
        已帶入 {{ formatClosureDate(copySource.date) }}的醫師勾選
      </div>
    </div>
    <slot-closure-calendar
      :value="dates"
      :closed-dates="closedDates"
      @input="setDates"
    />

    <template v-if="dates.length">
      <div class="closure-doctors-header mt-5 mb-2">
        <div class="font-weight-bold">勾選醫師</div>
        <v-switch
          v-if="dates.length > 1"
          v-model="sameForAll"
          class="ma-0 pa-0"
          label="所有日期使用相同設定"
          color="tiffany-7"
          hide-details
          dense
          @change="onSameForAllChange"
        />
      </div>
      <slot-closure-doctor-table
        :doctors="doctors"
        :columns="columns"
        @toggle-all="toggleAll"
        @toggle-doctor="toggleDoctor"
      />
    </template>

    <confirm-dialog
      v-if="sameConfirm"
      :value="true"
      :title="`所有日期改用 ${formatClosureDate(dates[0])}的勾選？`"
      confirm-btn-text="套用"
      persistent
      @confirm="applySameForAll"
      @cancel="cancelSameForAll"
    />

    <template #submit>
      <v-btn
        color="primary"
        :disabled="!validDates.length"
        :loading="saving"
        @click="submit"
      >{{ submitLabel }}</v-btn>
    </template>
  </slot-closure-dialog-shell>
</template>

<script>
import ConfirmDialog from "@/components/utils/ConfirmDialog.vue";
import SlotClosureCalendar from "./SlotClosureCalendar.vue";
import SlotClosureDialogShell from "./SlotClosureDialogShell.vue";
import SlotClosureDoctorTable from "./SlotClosureDoctorTable.vue";
import {
  buildExcludedCodes,
  copyableCodes,
  formatClosureDate,
  selectableCodes,
  toggleAllCodes
} from "@/utils/slotClosure";

export default {
  name: "SlotClosureAddDialog",

  components: {ConfirmDialog, SlotClosureCalendar, SlotClosureDialogShell, SlotClosureDoctorTable},

  props: {
    value: {
      type: Boolean,
      required: true
    },
    doctors: {
      type: Array,
      required: true
    },
    closedDates: {
      type: Array,
      required: true
    },
    copySource: {
      type: Object,
      default: null
    },
    saving: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      dates: [],
      checked: {},
      sameForAll: true,
      sameConfirm: false,
      formatClosureDate
    }
  },

  computed: {
    defaultCodes() {
      return this.copySource
        ? copyableCodes(this.doctors, this.copySource)
        : selectableCodes(this.doctors)
    },
    // 資料一律每天一份，相同設定只是少顯示幾欄，送出邏輯才不用分兩種
    synced() {
      return this.sameForAll && !this.sameConfirm
    },
    columns() {
      if (this.synced) {
        const [first] = this.dates
        return [{
          key: first,
          label: this.dates.length > 1 ? `全部 ${this.dates.length} 天` : formatClosureDate(first),
          checkedCodes: this.checked[first]
        }]
      }
      return this.dates.map(date => ({
        key: date,
        label: formatClosureDate(date),
        checkedCodes: this.checked[date]
      }))
    },
    datesDiffer() {
      const first = this.checked[this.dates[0]]
      return this.dates.some(date => {
        const codes = this.checked[date]
        return codes.length !== first.length || codes.some(code => !first.includes(code))
      })
    },
    validDates() {
      return this.dates.filter(date => this.checked[date].length > 0)
    },
    submitLabel() {
      const count = this.validDates.length
      if (count === 0) return '關閉空檔'
      if (count === 1) return `關閉 ${formatClosureDate(this.validDates[0])}的空檔`
      return `關閉 ${count} 天的空檔`
    }
  },

  watch: {
    // 別人先關了同一天時後端回 409：已選的這天在月曆上點不掉，要在這裡移除，否則永遠送不出去
    closedDates(closed) {
      this.setDates(this.dates.filter(date => !closed.includes(date)))
    }
  },

  methods: {
    setDates(dates) {
      const shared = this.sameForAll && this.dates.length ? this.checked[this.dates[0]] : this.defaultCodes
      const checked = {}
      dates.forEach(date => {
        checked[date] = this.checked[date] || shared
      })
      this.checked = checked
      this.dates = dates
    },
    setCodes(date, codes) {
      const targets = this.synced ? this.dates : [date]
      targets.forEach(target => {
        this.checked[target] = codes
      })
    },
    toggleDoctor(date, code) {
      const codes = this.checked[date]
      this.setCodes(date, codes.includes(code)
        ? codes.filter(selected => selected !== code)
        : [...codes, code])
    },
    toggleAll(date) {
      this.setCodes(date, toggleAllCodes(this.doctors, this.checked[date]))
    },
    // 開回相同設定只能留一份勾選，每天不同時要先問，不能默默蓋掉
    onSameForAllChange(same) {
      if (!same) return
      if (this.datesDiffer) {
        this.sameConfirm = true
      }
    },
    applySameForAll() {
      const codes = this.checked[this.dates[0]]
      this.dates.forEach(date => {
        this.checked[date] = codes
      })
      this.sameConfirm = false
    },
    cancelSameForAll() {
      this.sameForAll = false
      this.sameConfirm = false
    },
    submit() {
      this.$emit('submit', this.validDates.map(date => ({
        date,
        excluded_doctor_codes: buildExcludedCodes(this.doctors, this.checked[date])
      })))
    }
  }
}
</script>

<style scoped>
.closure-doctors-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  min-height: 32px;
}
</style>
